// Costanti condivise: colonne Kanban, priorità, colori note, valute
import type {
  KanbanColumnDef,
  NoteColor,
  Priority,
  PersonalTask,
  ProfitTask,
  TaskStatus,
  ProfitStatus,
  AppSettings,
} from '@/types';

// ------------------------------------------------------------
// PERSONAL TASKS - colonne Kanban
// ------------------------------------------------------------
export const PERSONAL_COLUMNS: KanbanColumnDef<PersonalTask>[] = [
  { id: 'col-todo', title: 'Da fare', status: 'todo', color: 'bg-slate-500' },
  { id: 'col-in-progress', title: 'In corso', status: 'in_progress', color: 'bg-blue-500' },
  { id: 'col-review', title: 'In revisione', status: 'review', color: 'bg-amber-500' },
  { id: 'col-done', title: 'Completato', status: 'done', color: 'bg-emerald-500' },
  { id: 'col-cancelled', title: 'Annullato', status: 'cancelled', color: 'bg-rose-500' },
];

// ------------------------------------------------------------
// PROFIT TASKS - colonne Kanban (pipeline commerciale)
// ------------------------------------------------------------
export const PROFIT_COLUMNS: KanbanColumnDef<ProfitTask>[] = [
  { id: 'col-lead', title: 'Lead', status: 'lead', color: 'bg-slate-500' },
  { id: 'col-quoted', title: 'Preventivato', status: 'quoted', color: 'bg-indigo-500' },
  { id: 'col-in-progress', title: 'In corso', status: 'in_progress', color: 'bg-blue-500' },
  { id: 'col-invoiced', title: 'Fatturato', status: 'invoiced', color: 'bg-amber-500' },
  { id: 'col-paid', title: 'Pagato', status: 'paid', color: 'bg-emerald-500' },
  { id: 'col-cancelled', title: 'Annullato', status: 'cancelled', color: 'bg-rose-500' },
];

// ------------------------------------------------------------
// PRIORITÀ
// ------------------------------------------------------------
export const PRIORITY_META: Record<Priority, { label: string; color: string; dot: string; order: number }> = {
  urgent: { label: 'Urgente', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300', dot: 'bg-rose-500', order: 4 },
  high: { label: 'Alta', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', dot: 'bg-orange-500', order: 3 },
  medium: { label: 'Media', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', dot: 'bg-amber-500', order: 2 },
  low: { label: 'Bassa', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', dot: 'bg-slate-400', order: 1 },
};

export const PRIORITIES: Priority[] = ['low', 'medium', 'high', 'urgent'];

// ------------------------------------------------------------
// STATO TASK (label)
// ------------------------------------------------------------
export const PERSONAL_STATUS_META: Record<TaskStatus, { label: string; color: string }> = {
  todo: { label: 'Da fare', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
  in_progress: { label: 'In corso', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  review: { label: 'In revisione', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  done: { label: 'Completato', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  cancelled: { label: 'Annullato', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
};

export const PROFIT_STATUS_META: Record<ProfitStatus, { label: string; color: string }> = {
  lead: { label: 'Lead', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
  quoted: { label: 'Preventivato', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  in_progress: { label: 'In corso', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  invoiced: { label: 'Fatturato', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  paid: { label: 'Pagato', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  cancelled: { label: 'Annullato', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
};

// ------------------------------------------------------------
// NOTE COLORS - palette
// ------------------------------------------------------------
export const NOTE_COLORS: Record<NoteColor, { label: string; bg: string; border: string; dot: string }> = {
  default: { label: 'Default', bg: 'bg-white dark:bg-slate-800', border: 'border-slate-200 dark:border-slate-700', dot: 'bg-slate-400' },
  red: { label: 'Rosso', bg: 'bg-red-50 dark:bg-red-950/40', border: 'border-red-200 dark:border-red-900', dot: 'bg-red-400' },
  orange: { label: 'Arancione', bg: 'bg-orange-50 dark:bg-orange-950/40', border: 'border-orange-200 dark:border-orange-900', dot: 'bg-orange-400' },
  yellow: { label: 'Giallo', bg: 'bg-yellow-50 dark:bg-yellow-950/40', border: 'border-yellow-200 dark:border-yellow-900', dot: 'bg-yellow-400' },
  green: { label: 'Verde', bg: 'bg-green-50 dark:bg-green-950/40', border: 'border-green-200 dark:border-green-900', dot: 'bg-green-400' },
  teal: { label: 'Teal', bg: 'bg-teal-50 dark:bg-teal-950/40', border: 'border-teal-200 dark:border-teal-900', dot: 'bg-teal-400' },
  blue: { label: 'Blu', bg: 'bg-blue-50 dark:bg-blue-950/40', border: 'border-blue-200 dark:border-blue-900', dot: 'bg-blue-400' },
  purple: { label: 'Viola', bg: 'bg-purple-50 dark:bg-purple-950/40', border: 'border-purple-200 dark:border-purple-900', dot: 'bg-purple-400' },
  pink: { label: 'Rosa', bg: 'bg-pink-50 dark:bg-pink-950/40', border: 'border-pink-200 dark:border-pink-900', dot: 'bg-pink-400' },
};

export const NOTE_COLOR_KEYS: NoteColor[] = ['default', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink'];

// ------------------------------------------------------------
// VALUTE supportate
// ------------------------------------------------------------
export const CURRENCIES = [
  { code: 'EUR', label: 'Euro (€)', symbol: '€' },
  { code: 'USD', label: 'Dollar USA ($)', symbol: '$' },
  { code: 'GBP', label: 'Sterlina (£)', symbol: '£' },
  { code: 'CHF', label: 'Franco Svizzero (CHF)', symbol: 'CHF' },
  { code: 'JPY', label: 'Yen (¥)', symbol: '¥' },
  { code: 'CNY', label: 'Yuan (¥)', symbol: '¥' },
];

// ------------------------------------------------------------
// GIORNI SETTIMANA
// ------------------------------------------------------------
export const WEEKDAYS_META: { value: number; label: string; short: string; color: string }[] = [
  { value: 1, label: 'Lunedì', short: 'Lun', color: 'bg-blue-500' },
  { value: 2, label: 'Martedì', short: 'Mar', color: 'bg-blue-500' },
  { value: 3, label: 'Mercoledì', short: 'Mer', color: 'bg-blue-500' },
  { value: 4, label: 'Giovedì', short: 'Gio', color: 'bg-blue-500' },
  { value: 5, label: 'Venerdì', short: 'Ven', color: 'bg-blue-500' },
  { value: 6, label: 'Sabato', short: 'Sab', color: 'bg-emerald-500' },
  { value: 0, label: 'Domenica', short: 'Dom', color: 'bg-rose-500' },
];

export const WEEKDAY_PRESETS = [
  { id: 'all', label: 'Tutti i giorni', days: [0, 1, 2, 3, 4, 5, 6] },
  { id: 'weekdays', label: 'Lun-Ven', days: [1, 2, 3, 4, 5] },
  { id: 'weekend', label: 'Solo weekend', days: [0, 6] },
];

// ------------------------------------------------------------
// DEFAULT SETTINGS
// ------------------------------------------------------------
export const DEFAULT_SETTINGS: AppSettings = {
  theme: 'system',
  default_currency: 'EUR',
  notifications_enabled: true,
  quiet_hours_start: null,
  quiet_hours_end: null,
  last_sync_at: null,
};
